import { ChangeDetectionStrategy, Component, OnInit, inject, signal, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { InventoryType } from '@servicios/inventory/inventory.service';
import { MatDialog } from '@angular/material/dialog';
import { OnboardingService } from '@servicios/onboarding.service';
import { InventoryQuickGuideComponent } from 'app/_sharedComponents/inventory-quick-guide/inventory-quick-guide.component';
import { QuickActionsComponent } from './components/quick-actions/quick-actions.component';
import { Router } from '@angular/router';
import { BulkImportService, BulkImportColumn } from 'app/_servicios/bulk-import.service';
import { AuthenticationService } from '@servicios/authentication.service';
import { Roles } from '@modelos/interfaces/operation-result-types';

@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class InventoryComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private onboardingService = inject(OnboardingService);
  private bulkImportService = inject(BulkImportService);
  private authService = inject(AuthenticationService);
  
  @ViewChild('quickActions') quickActions!: QuickActionsComponent;
  selectedType = signal<InventoryType>('items');
  showTypePicker = signal(false);

  get canManageInventory(): boolean {
    const roles = this.authService.CURRENT_USER_ROLES || [];
    return roles.includes(Roles.ADMIN) || roles.includes(Roles.INVENTORY_MANAGER);
  }

  // Categoría seleccionada para pasar al TableItemsComponent
  pendingType = signal<string | null>(null);
  // Incrementado cada vez que se completa un movimiento rápido; los hijos reaccionan via effect()
  refreshTrigger = signal(0);
  userBranches = signal<{id: string, name: string}[]>([]);
  selectedBranchId = signal<string | null>(null);

  allTypes: { label: string, value: InventoryType }[] = [
    { label: 'Proveedores', value: 'supplier' },
    { label: 'Presentaciones', value: 'form' },
    { label: 'Regulaciones', value: 'regulations' },
    { label: 'Marcas', value: 'brand' },
    { label: 'Ubicaciones', value: 'location' },
    { label: 'Propósitos de Uso', value: 'usage-purpose' },
    { label: 'Tipos de Residuos', value: 'waste-type' },
    { label: 'Conceptos de Costo', value: 'cost-concepts' },
    { label: 'Siglas de Estudio', value: 'study-tags' },
    { label: 'Estudios', value: 'studies' },
    { label: 'Artículos', value: 'items' },
    { label: 'Transferencias', value: 'stock-transfers' },
    { label: 'Movimientos', value: 'stock-movements' },
    { label: 'Tipos de Formulación', value: 'formulation-types' }
  ];

  types = signal<{ label: string, value: InventoryType }[]>([]);

  itemTypes = [
    { label: 'Producto', value: 'PRODUCT', desc: 'Bienes tangibles para la venta o uso.', icon: 'pi pi-box' },
    { label: 'Servicio', value: 'SERVICE', desc: 'Actividades o prestaciones de valor.', icon: 'pi pi-cog' },
    { label: 'Consumible', value: 'CONSUMABLE', desc: 'Insumos que se agotan con el uso.', icon: 'pi pi-filter' },
    { label: 'Estándar', value: 'STANDARD', desc: 'Patrones de referencia certificados.', icon: 'pi pi-check-circle' },
    { label: 'Reactivo', value: 'REACTIVE', desc: 'Sustancias para análisis químicos.', icon: 'pi pi-sparkles' },
    { label: 'Repuesto', value: 'SPARE_PART', desc: 'Piezas para mantenimiento.', icon: 'pi pi-wrench' },
    { label: 'Ítem de Ensayo', value: 'TEST_ITEM', desc: 'Muestras de ensayo con composición y balance de masa (BPL).', icon: 'pi pi-filter' }
  ];

  ngOnInit() {
    const branches = (this.authService.currentUserValue as any)?.branches || [];
    this.userBranches.set(branches);
    if (branches.length > 0) {
      this.selectedBranchId.set(branches[0].id);
    }

    this.route.data.subscribe(data => {
      const mode = data['mode'];
      if (mode === 'items') {
        this.types.set(this.allTypes.filter(t => t.value === 'items'));
        this.selectedType.set('items');
      } else if (mode === 'movements') {
        this.types.set(this.allTypes.filter(t => t.value === 'stock-movements' || t.value === 'stock-transfers'));
        this.selectedType.set('stock-movements');
      } else if (mode === 'transfers') {
        this.types.set(this.allTypes.filter(t => t.value === 'stock-movements' || t.value === 'stock-transfers'));
        this.selectedType.set('stock-transfers');
      } else if (mode === 'general') {
        this.types.set(this.allTypes.filter(t => t.value !== 'items' && t.value !== 'stock-movements' && t.value !== 'stock-transfers'));
        this.selectedType.set('supplier');
      } else {
        this.types.set(this.allTypes);
      }
    });

    this.startInventoryTour();
  }

  startInventoryTour(force = false) {
    this.onboardingService.startTour({
      id: 'inventory-basics',
      steps: [
        {
          selector: '#tour-inv-header',
          title: '📦 Gestión de Inventario',
          content: 'Bienvenido. Desde aquí controlás el stock completo del laboratorio: reactivos, consumibles, estándares, repuestos y más.',
          position: 'bottom'
        },
        {
          selector: '#tour-inv-tabs',
          title: '📂 Categorías',
          content: 'Navegá entre Artículos, Movimientos de Stock y las tablas de configuración: Proveedores, Marcas, Presentaciones, Regulaciones, Ubicaciones y Propósitos de Uso.',
          position: 'bottom'
        },
        {
          selector: '#tour-inv-stats',
          title: '⚠️ Indicadores de Stock en Tiempo Real',
          content: 'Estas tres tarjetas muestran cuántos artículos tienen alerta de stock, stock crítico (bajo el mínimo) y exceso de stock (sobre el máximo). Se actualizan con cada carga.',
          position: 'bottom'
        },
        {
          selector: '#tour-inv-report-btn',
          title: '📄 Reporte por Regulación',
          content: 'Generá un reporte PDF del stock filtrado por regulación (ej: ANMAT, ISO 17025). Útil para auditorías e informes de cumplimiento.',
          position: 'bottom'
        },
        {
          selector: '#tour-inv-header',
          title: '✅ ¡Listo para usar!',
          content: 'Hacé doble clic en cualquier fila de la tabla para ver la hoja de vida completa del artículo: stock, historial de movimientos, previsión de consumo y equipos vinculados. Podés reiniciar esta guía con el botón "Ayuda".',
          position: 'bottom'
        }
      ]
    }, force);
  }

  setType(type: InventoryType) {
    this.selectedType.set(type);
    this.showTypePicker.set(false);
    this.pendingType.set(null);
  }

  handleNewRequest() {
    this.pendingType.set(null);
    this.showTypePicker.set(true);
  }

  selectItemType(type: string) {
    this.pendingType.set(type);
    this.showTypePicker.set(false);
  }

  openGuide() {
    this.startInventoryTour(true);
  }

  onQuickMovementSuccess() {
    // Increment the trigger so children with an effect() on refreshTrigger reload their data
    this.refreshTrigger.update(v => v + 1);
  }

  goToBulkImport() {
    const type = this.selectedType();
    const typeObj = this.types().find(t => t.value === type);
    
    let columns: BulkImportColumn[] = [];
    
    if (type === 'supplier' || type === 'form' || type === 'brand' || type === 'location' || 
        type === 'usage-purpose' || type === 'waste-type' || type === 'cost-concepts' || type === 'study-tags') {
      columns = [
        { field: 'name', header: 'Nombre', required: true, type: 'string' },
        { field: type === 'supplier' ? 'internal_code' : 'code', header: 'Código', required: true, type: 'string' },
        { field: 'description', header: 'Descripción', required: false, type: 'string' }
      ];
    } else if (type === 'regulations') {
      columns = [
        { field: 'name', header: 'Nombre', required: true, type: 'string' },
        { field: 'code', header: 'Código', required: true, type: 'string' },
        { field: 'description', header: 'Descripción', required: false, type: 'string' },
        { field: 'controller_organization', header: 'Org. Controlador', required: false, type: 'string' }
      ];
    } else if (type === 'studies') {
      columns = [
        { field: 'code', header: 'Código', required: true, type: 'string' },
        { field: 'description', header: 'Descripción', required: false, type: 'string' }
      ];
    } else if (type === 'formulation-types') {
      columns = [
        { field: 'name', header: 'Nombre', required: true, type: 'string' },
        { field: 'code', header: 'Código', required: true, type: 'string' },
        { field: 'classification', header: 'Clasificación', required: true, type: 'dropdown', options: [
          {label: 'Grado Técnico / Técnico Concentrado', value: 'Grado Técnico / Técnico Concentrado'},
          {label: 'Formulados', value: 'Formulados'}
        ]},
        { field: 'study_suffix', header: 'Sufijo BPL', required: false, type: 'string' },
        { field: 'allow_multiple_actives', header: 'Múltiples Activos', required: false, type: 'boolean' }
      ];
    } else if (type === 'items') {
      columns = [
        { field: 'name', header: 'Nombre / Descripción', required: true, type: 'string' },
        { field: 'internal_code', header: 'Código Interno', required: false, type: 'string' },
        { field: 'brand_id', header: 'Marca', required: false, type: 'string' },
        { field: 'supplier_id', header: 'Proveedor', required: false, type: 'string' },
        { field: 'item_type', header: 'Tipo', required: true, type: 'dropdown', options: [
          {label: 'Producto', value: 'PRODUCT'},
          {label: 'Servicio', value: 'SERVICE'}
        ] }
      ];
    }

    if (columns.length === 0) return;

    // Model name map since backend uses specific names for Prisma
    const modelMap: Record<string, string> = {
      'supplier': 'suppliers',
      'form': 'presentations',
      'regulations': 'regulations',
      'brand': 'brands',
      'location': 'locations',
      'usage-purpose': 'usagePurposes',
      'waste-type': 'wasteTypes',
      'cost-concepts': 'costConcepts',
      'study-tags': 'studyTags',
      'items': 'items',
      'studies': 'studies',
      'formulation-types': 'formulationTypes'
    };

    this.bulkImportService.setState({
      model: modelMap[type] || type,
      title: typeObj?.label || type,
      columns: columns,
      returnUrl: this.router.url,
      importStrategy: 'inventory-ms',
      inventoryType: type as any
    });

    this.router.navigate(['/bulk-import']);
  }
}
